import { useEffect, useState } from "react";
import Button from "@/components/base/Button";
import type { Bodega } from "@/types/content";
import { useT } from "@/lib/useSiteText";

interface Props {
  bodega: Bodega | null;
  onClose: () => void;
  whatsappLink: (msg?: string) => string;
}

export default function BodegaModal({ bodega, onClose, whatsappLink }: Props) {
  const t = useT();
  const [displayed, setDisplayed] = useState<Bodega | null>(null);
  const [visible, setVisible] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (bodega) {
      setDisplayed(bodega);
      setActiveImage(0);
      setVisible(false);
      document.body.style.overflow = "hidden";
      // Se necesitan dos frames: el primero deja pintado el estado inicial
      // (oculto), el segundo recien activa la transicion hacia visible.
      // Con uno solo, el navegador a veces pinta ambos estados juntos y la
      // animacion no se alcanza a ver.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    if (displayed) {
      setVisible(false);
      const t = window.setTimeout(() => {
        setDisplayed(null);
        document.body.style.overflow = "";
      }, 300);
      return () => window.clearTimeout(t);
    }
  }, [bodega]);

  if (!displayed) return null;
  const b = displayed;
  const galeria = [{ url: b.imagen_url, alt: b.imagen_alt }, ...b.imagenes.filter((img) => img.url)].filter(
    (img) => img.url
  );
  const activa = galeria[activeImage] ?? galeria[0];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/60 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={b.nombre}
        className={`relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-background-50 shadow-xl transition-all duration-300 ${
          visible ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-3"
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-background-50 border border-background-200 text-foreground-700 flex items-center justify-center hover:bg-background-100 transition cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="p-5 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              {activa ? (
                <img
                  src={activa.url}
                  alt={activa.alt || b.nombre}
                  className="w-full h-64 md:h-96 object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-64 md:h-96 rounded-lg bg-background-200" />
              )}
              {galeria.length > 1 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {galeria.map((img, i) => (
                    <button
                      key={img.url + i}
                      type="button"
                      onClick={() => setActiveImage(i)}
                      className={`rounded-md overflow-hidden border-2 cursor-pointer ${
                        i === activeImage ? "border-primary-500" : "border-transparent"
                      }`}
                    >
                      <img src={img.url} alt="" className="w-full h-16 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <span
                className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                  b.disponible ? "bg-primary-100 text-primary-800" : "bg-background-200 text-foreground-700"
                }`}
              >
                {b.disponible ? t("bodegas.badge_disponible") : t("bodegas.badge_ocupado")}
              </span>
              <h2 className="mt-4 font-heading font-bold text-2xl text-foreground-950">{b.nombre}</h2>
              <p className="mt-2 text-sm text-foreground-500">
                <span className="font-heading font-bold text-2xl text-foreground-900">{b.m2} m²</span>
              </p>
              <p className="mt-1 text-foreground-600">
                {t("bodegas.precio_desde")}{" "}
                <strong className="text-foreground-900">UF {b.valor_uf.toLocaleString("es-CL")}</strong>{" "}
                {t("bodegas.precio_sufijo")}
              </p>

              <ul className="mt-6 space-y-2.5">
                {b.caracteristicas.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-sm text-foreground-700">
                    <span className="w-5 h-5 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-xs" />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3">
                <Button
                  href={whatsappLink(t("wa.cotizar_bodega", { nombre: b.nombre }))}
                  variant="accent"
                  icon="ri-whatsapp-line"
                >
                  {t("bodegas.detalle.whatsapp")}
                </Button>
                <Button to="/contacto" variant="outline">
                  {t("bodegas.detalle.formulario")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
